import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { EngagementSpotlight, SpotlightManager, SpotlightTarget } from '@atlassiansox/engagekit-ts';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import {
	DefinitionUpdateConfirmationFlag,
	EditDefinitionModal,
} from '../../v2/definitions/curated/edit-definition-modal';
import { useAIAnswerContext } from '../ai-answer-context';
import {
	onCuratedDefinitionEditButtonClicked,
	onCuratedDefinitionEditButtonShown,
} from '../analytics';
import { EditDefinitionWrapper } from '../styled';

const messages = defineMessages({
	edit_definition_icon_tooltip: {
		id: 'search_ai.curated-definition-edit-def-icon-tooltip',
		defaultMessage: 'Edit definition',
		description: 'Tooltip for edit definition icon',
	},
	edit_definition_prompt_text: {
		id: 'search_ai.edit_definition_prompt_text',
		defaultMessage: 'Not quite right? Edit this definition',
		description: 'Prompt text on button for users to edit a definition',
	},
	edit_definition_prompt_text_updated: {
		id: 'search_ai.edit_definition_prompt_text_updated',
		defaultMessage: 'Edit this definition',
		description: 'Prompt text on button for users to edit a definition in menu dropdown',
	},
});

const EditIconStyled = () => {
	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	return (
		<EditIcon
			color={token('color.icon.subtle')}
			label=""
			size={serveDefinitionUXUpdates ? 'small' : 'medium'}
		/>
	);
};

interface EditDefinitionProps {
	onClose?: () => void;
}

export const EditDefinition = ({ onClose }: EditDefinitionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent, isAnswered, hasError, readingAidsOptions, commonAttributes } =
		useAIAnswerContext();

	const { definitionCurationOptions } = readingAidsOptions || {};

	const editButtonSpotlightId = 'ai-definitions-edit-button';

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	const [showEditModal, setShowEditModal] = useState(false);
	const [displaySuccessFlag, setDisplaySuccessFlag] = useState(false);

	const closeEditModal = useCallback(() => {
		setShowEditModal(false);
		onClose?.();
	}, [setShowEditModal, onClose]);

	const openEditModal = useCallback(() => {
		fireAnalyticsEvent(onCuratedDefinitionEditButtonClicked(commonAttributes));
		setShowEditModal(true);
	}, [commonAttributes, fireAnalyticsEvent]);

	useEffect(() => {
		if (isAnswered) {
			fireAnalyticsEvent(onCuratedDefinitionEditButtonShown(commonAttributes));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Fragment>
			{!hasError && (
				<SpotlightManager>
					<SpotlightTarget name={editButtonSpotlightId}>
						{serveDefinitionUXUpdates ? (
							<Button
								appearance="subtle"
								iconBefore={EditIconStyled}
								onClick={openEditModal}
								spacing="compact"
							>
								<Text color="color.text.subtle" size="small">
									{formatMessage(messages.edit_definition_prompt_text_updated)}
								</Text>
							</Button>
						) : (
							<EditDefinitionWrapper>
								<Button appearance="subtle" iconBefore={EditIconStyled} onClick={openEditModal}>
									{formatMessage(messages.edit_definition_prompt_text)}
								</Button>
							</EditDefinitionWrapper>
						)}
					</SpotlightTarget>
					<EngagementSpotlight isMigrated engagementId={editButtonSpotlightId} />
				</SpotlightManager>
			)}
			<ModalTransition>
				{showEditModal && definitionCurationOptions && (
					<EditDefinitionModal
						curatedDefinitionScopeConfig={definitionCurationOptions.curatedDefinitionScopeConfig}
						closeEditModal={closeEditModal}
						onSaveEditModal={() => setDisplaySuccessFlag(true)}
					/>
				)}
			</ModalTransition>
			{displaySuccessFlag && (
				<DefinitionUpdateConfirmationFlag onDismissed={() => setDisplaySuccessFlag(false)} />
			)}
		</Fragment>
	);
};
